/**
 * DAS/ARR auto-repeat, as a pure, clock-free state machine.
 *
 * `docs/DESIGN.md`: DAS default 140ms, ARR default 28ms. "A tap moves exactly
 * one cell; a hold repeats after DAS at ARR rate." The engine has no idea DAS
 * or ARR exist (`docs/prompts/02-client.md`: "DAS and ARR are not here and
 * will not be: they belong to phase 02") — this is where they live.
 *
 * `press()` fires the tap immediately and starts the hold timer. `advance(dt)`
 * is called once per engine tick with that tick's elapsed milliseconds, and
 * returns how many *additional* repeats have become due since the last call —
 * zero until `dasMs` has elapsed, then one every `arrMs` after that. Nothing
 * here reads a clock: the caller supplies every millisecond.
 */
export class AutoRepeat {
  private heldMs = 0;
  private pressed = false;
  private repeatsFired = 0;

  constructor(
    private dasMs = 140,
    private arrMs = 28,
  ) {}

  /**
   * Change the timings without touching a hold in progress.
   *
   * The Settings steppers call this while a run is live. `heldMs` and
   * `repeatsFired` are left alone on purpose: the next `advance` simply
   * measures the same hold against the new numbers, and because it only ever
   * returns *newly* due repeats (never a negative count), a change that makes
   * fewer repeats due than have already fired pauses the repeat until the
   * hold catches up rather than firing a burst or moving the piece back.
   */
  configure(dasMs: number, arrMs: number): void {
    this.dasMs = dasMs;
    this.arrMs = arrMs;
  }

  /** True while the direction is held. */
  get isHeld(): boolean {
    return this.pressed;
  }

  /** Press: starts the hold timer and returns the one immediate move a tap makes. */
  press(): number {
    this.pressed = true;
    this.heldMs = 0;
    this.repeatsFired = 0;
    return 1;
  }

  /** Release: stops the hold timer. A tap that releases before `advance` runs never repeats. */
  release(): void {
    this.pressed = false;
    this.heldMs = 0;
    this.repeatsFired = 0;
  }

  /**
   * Advance the hold timer by `dtMs`. Returns the count of ARR-cadence
   * repeats newly due — 0 while still inside the DAS window, then 1 at the
   * instant DAS elapses, then 1 more every `arrMs` after that.
   */
  advance(dtMs: number): number {
    if (!this.pressed || dtMs <= 0) return 0;

    this.heldMs += dtMs;
    if (this.heldMs < this.dasMs) return 0;

    const pastDas = this.heldMs - this.dasMs;
    const arr = Math.max(1, this.arrMs);
    const totalRepeats = Math.floor(pastDas / arr) + 1;
    const newFires = totalRepeats - this.repeatsFired;
    if (newFires <= 0) return 0;

    this.repeatsFired = totalRepeats;
    return newFires;
  }
}
